// TeacherCalendar.tsx — CURRENT CORE CALENDAR (wrapped by Calendar2.tsx page)
// Do not remove; feature work builds here until a future rebuild plan is approved.
import React, { useEffect, useState, useRef, Component } from 'react'
import { useLocation } from 'react-router-dom'
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  serverTimestamp,
  onSnapshot,
  query,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from './providers/ThemeProvider'
import toast from 'react-hot-toast'
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  UserIcon,
  MusicIcon,
  AlertCircleIcon,
  CheckIcon,
  SaveIcon,
  PlusIcon,
  XIcon,
  BookIcon,
  InfoIcon,
  EditIcon,
  Trash2Icon,
  LoaderIcon,
  LockIcon,
  RefreshCwIcon,
  WifiOffIcon,
  ShieldIcon,
  HelpCircleIcon,
  ShieldAlertIcon,
  UserXIcon,
  KeyIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CopyIcon,
  AlertTriangleIcon,
} from 'lucide-react'
import { CustomTimeDropdown } from './CustomTimeDropdown'
import { useLessons } from '../src/hooks/useLessons'
import {
  toLocal,
  toUtcISOString,
  validateRange,
  ymdLocal,
} from '../src/utils/datetime'
import { DEBUG_CALENDAR, Lesson } from '../src/services/lessonsService'
import { TeacherCalendarWeek } from './calendar/TeacherCalendarWeek'
import { TeacherCalendarDay } from './calendar/TeacherCalendarDay'
import { LessonChip } from './calendar/LessonChip'
import { CalendarViewSelector } from './calendar/CalendarViewSelector'
import { LessonDetailsModal } from './LessonDetailsModal'
import { MonthYearPicker } from './calendar/MonthYearPicker'
// DIAGNOSTIC LOG - Prove this file is mounting
console.log('TeacherCalendar.tsx mounted — build marker: TC_2025_12_19_A')
// Define CalendarView type - add day view
type CalendarView = 'month' | 'week' | 'day'
// Define TeacherCalendarProps type
interface TeacherCalendarProps {
  devMode?: boolean // development mode (mock data)
  onCellClick?: (date: Date) => void // ADDED: callback for month cell clicks
  onTimeSlotClick?: (date: Date, hour: number, minute: number) => void // ADDED: callback for week time slot clicks
  initialDate?: string // YYYY-MM-DD string to navigate calendar to a specific date (e.g., from notification click)
}
// Default timezone - in a real app, this would come from user preferences
const DEFAULT_TIMEZONE = 'America/Los_Angeles'
// Add a security service for access control
const SecurityService = {
  // Check if user is authenticated and is a teacher
  canAccessTeacherCalendar: (currentUser: any, userType: string | null) => {
    if (!currentUser) return false
    if (userType !== 'teacher') return false
    return true
  },
  // Check if user can modify the calendar
  canModifyCalendar: (
    currentUser: any,
    userType: string | null,
    teacherId: string,
  ) => {
    if (!currentUser) return false
    if (userType !== 'teacher') return false
    if (currentUser.uid !== teacherId) return false
    return true
  },
  // Validate lesson data before saving
  validateLessonData: (lessonData: any) => {
    const errors: string[] = []
    // Required fields
    if (!lessonData.title || lessonData.title.trim() === '') {
      errors.push('Lesson title is required')
    }
    if (!lessonData.startAt) {
      errors.push('Start time is required')
    }
    if (!lessonData.endAt) {
      errors.push('End time is required')
    }
    // Validate student name if provided
    if (lessonData.studentName && lessonData.studentName.trim() === '') {
      errors.push('Student name cannot be empty if provided')
    }
    // Additional validations
    if (lessonData.startAt) {
      // Check if date is in the past
      const lessonDate = new Date(lessonData.startAt)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (lessonDate < today) {
        errors.push('Cannot schedule lessons in the past')
      }
    }
    return {
      isValid: errors.length === 0,
      errors,
    }
  },
  // Sanitize user input to prevent injection attacks
  sanitizeInput: (input: any) => {
    if (typeof input !== 'string') return input
    // Basic sanitization - remove script tags
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .trim()
  },
  // Sanitize an entire lesson object
  sanitizeLessonData: (lessonData: any) => {
    const sanitized: any = {
      ...lessonData,
    }
    // Sanitize string fields
    if (sanitized.title)
      sanitized.title = SecurityService.sanitizeInput(sanitized.title)
    if (sanitized.studentName)
      sanitized.studentName = SecurityService.sanitizeInput(
        sanitized.studentName,
      )
    if (sanitized.notes)
      sanitized.notes = SecurityService.sanitizeInput(sanitized.notes)
    if (sanitized.instrument)
      sanitized.instrument = SecurityService.sanitizeInput(sanitized.instrument)
    return sanitized
  },
}
// Error boundary typing
interface CalendarErrorBoundaryProps {
  children: React.ReactNode
}
interface CalendarErrorBoundaryState {
  hasError: boolean
  error: any
  errorInfo: any
}
// Create a dedicated error boundary for the calendar component
class CalendarErrorBoundary extends Component<
  CalendarErrorBoundaryProps,
  CalendarErrorBoundaryState
> {
  state: CalendarErrorBoundaryState = {
    hasError: false,
    error: null,
    errorInfo: null,
  }
  static getDerivedStateFromError(error: any) {
    return {
      hasError: true,
      error,
    }
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('Calendar component error:', error, errorInfo)
    this.setState({
      errorInfo,
    })
  }
  render() {
    const { hasError, error, errorInfo } = this.state
    const { children } = this.props
    // Use the useTheme hook through a consumer component
    return (
      <ThemeConsumer>
        {(isDark) => {
          if (hasError) {
            return (
              <div
                className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-8 text-center`}
              >
                <div className="flex flex-col items-center justify-center py-8">
                  <AlertCircleIcon className="h-16 w-16 text-red-500 mb-4" />
                  <h3
                    className={`${isDark ? 'text-white' : 'text-gray-800'} text-xl font-medium mb-2`}
                  >
                    Calendar Error
                  </h3>
                  <p
                    className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-md mb-6`}
                  >
                    Something went wrong displaying your calendar. Please try
                    refreshing the page.
                  </p>
                  <div className="mb-4">
                    <details
                      className={`text-sm text-left ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      <summary>Technical Details</summary>
                      <pre className="mt-2 p-2 rounded bg-black/20 overflow-auto text-xs">
                        {error && error.toString()}
                        {errorInfo && errorInfo.componentStack}
                      </pre>
                    </details>
                  </div>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-[#DC2626] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <RefreshCwIcon className="h-4 w-4 mr-2 inline" />
                    Refresh Page
                  </button>
                </div>
              </div>
            )
          }
          return <>{children}</>
        }}
      </ThemeConsumer>
    )
  }
}
// Helper component to consume theme in class components
const ThemeConsumer = ({
  children,
}: {
  children: (isDark: boolean) => React.ReactNode
}) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  return <>{children(isDark)}</>
}
// Helper functions
const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate()
}
const getFirstDayOfMonth = (year: number, month: number) => {
  return new Date(year, month, 1).getDay()
}
const isToday = (date: Date) => {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}
// Helper function to format time (24h -> 12h)
const formatTime = (time24h: string): string => {
  if (!time24h) return ''
  const [hours, minutes] = time24h.split(':')
  const hour = parseInt(hours, 10)
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${hour12}:${minutes} ${suffix}`
}
// ADDED: Helper function to format time in 12h format (used by convertLessonToDetails)
const formatTime12h = (time24h: string): string => {
  if (!time24h) return ''
  const [hours, minutes] = time24h.split(':')
  const hour = parseInt(hours, 10)
  const suffix = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
  return `${hour12}:${minutes} ${suffix}`
}
// Helper function to format time range
const formatTimeRange = (startTime: string, endTime: string): string => {
  const start = formatTime(startTime)
  if (!endTime) return start
  const end = formatTime(endTime)
  return `${start}–${end}`
}
// Helper to check if text would be better as light or dark based on background color
const shouldUseLightText = (bgColor: string): boolean => {
  // Convert hex to RGB
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)
  // Calculate luminance - standard formula
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  // Return true for light text if background is dark
  return luminance < 0.7
}
// Add helper function to get color based on instrument
const getInstrumentColor = (instrument: string): string => {
  const instrumentColors: Record<string, string> = {
    Piano: '#5BB5E0',
    Guitar: '#81C784',
    Violin: '#F5D76E',
    Voice: '#F48FB1',
    Drums: '#BA68C8',
    Flute: '#4DB6AC',
    Saxophone: '#FF9E7A',
    Trumpet: '#FFB85C',
    Cello: '#9575CD',
    Bass: '#7986CB', // Kept - already balanced
  }
  return instrumentColors[instrument] || '#B0BEC5' // Default gray if instrument not found
}
// ADDED: Index-based color palette for lesson chips
const LESSON_CHIP_COLORS = [
  '#A78BFA',
  '#6EE7B7',
  '#7DD3FC',
  '#FCA5A5',
  '#5EEAD4',
  '#FCD34D',
  '#FDA4AF',
  '#A5B4FC', // Indigo
]
// ADDED: Helper function to get lesson chip color by index
const getLessonChipColor = (index: number): string => {
  return LESSON_CHIP_COLORS[index % LESSON_CHIP_COLORS.length]
}
// Helper function to format date to ISO string for range calculations
const getDateRangeForMonth = (
  date: Date,
): {
  fromISOUtc: string
  toISOUtc: string
} => {
  const year = date.getFullYear()
  const month = date.getMonth()
  // FIXED: Use Date objects with local time components (like Dashboard)
  // This correctly converts local midnight to UTC without double-applying offset
  const startOfMonth = new Date(year, month, 1)
  startOfMonth.setHours(0, 0, 0, 0) // Local midnight on 1st
  const endOfMonth = new Date(year, month + 1, 0) // Last day of month
  endOfMonth.setHours(23, 59, 59, 999) // Local end of day
  // Convert to UTC using .toISOString() (correct conversion)
  const fromISOUtc = startOfMonth.toISOString()
  const toISOUtc = endOfMonth.toISOString()
  console.log('✅ [DATE RANGE FIX] Month range computed:', {
    displayMonth: `${year}-${String(month + 1).padStart(2, '0')}`,
    startOfMonthLocal: startOfMonth.toLocaleString('en-US', {
      timeZone: DEFAULT_TIMEZONE,
    }),
    endOfMonthLocal: endOfMonth.toLocaleString('en-US', {
      timeZone: DEFAULT_TIMEZONE,
    }),
    startUTC: fromISOUtc,
    endUTC: toISOUtc,
    timezone: DEFAULT_TIMEZONE,
  })
  return { fromISOUtc, toISOUtc }
}
// Helper function to get date range for a week
const getDateRangeForWeek = (
  date: Date,
): {
  fromISOUtc: string
  toISOUtc: string
  weekDays: Date[]
} => {
  const currentDay = date.getDay() // 0 = Sunday, 1 = Monday, etc.
  const diff = date.getDate() - currentDay
  // Start of week (Sunday)
  const startOfWeek = new Date(date)
  startOfWeek.setDate(diff)
  startOfWeek.setHours(0, 0, 0, 0)
  // End of week (Saturday)
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 6)
  endOfWeek.setHours(23, 59, 59, 999)
  // Create an array of the week days
  const weekDays: Date[] = []
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek)
    day.setDate(startOfWeek.getDate() + i)
    weekDays.push(day)
  }
  return {
    fromISOUtc: startOfWeek.toISOString(),
    toISOUtc: endOfWeek.toISOString(),
    weekDays,
  }
}
// Helper function to get date range for a single day
const getDateRangeForDay = (
  date: Date,
): {
  fromISOUtc: string
  toISOUtc: string
} => {
  // Start of day (midnight)
  const startOfDay = new Date(date)
  startOfDay.setHours(0, 0, 0, 0)
  // End of day (23:59:59.999)
  const endOfDay = new Date(date)
  endOfDay.setHours(23, 59, 59, 999)
  return {
    fromISOUtc: startOfDay.toISOString(),
    toISOUtc: endOfDay.toISOString(),
  }
}
// Add this helper function to format dates consistently
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}
// Debug flag - forced to true for visibility testing
const DEBUG_CALENDAR = false // Set to false to hide debug elements including the static red stripe
export const TeacherCalendar: React.FC<TeacherCalendarProps> = ({
  devMode = false,
  onCellClick,
  onTimeSlotClick,
  initialDate,
}) => {
  // Get theme from global theme provider
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  // Properly destructure auth context with loading state
  const { currentUser, userType, loading: authLoading } = useAuth() as any
  const location = useLocation() // Add this to access URL query parameters
  const [view, setView] = useState<CalendarView>(() => {
    // Restore view from localStorage on initial mount
    const savedView = localStorage.getItem('calendar-view')
    if (savedView === 'month' || savedView === 'week' || savedView === 'day') {
      return savedView as CalendarView
    }
    return 'month' // Default to month view
  })
  const [currentDate, setCurrentDate] = useState(() => {
    // If initialDate is provided (e.g., from notification click), use it
    if (initialDate) {
      const [y, m, d] = initialDate.split('-').map(Number)
      if (y && m && d) {
        return new Date(y, m - 1, d)
      }
    }
    return new Date()
  })
  // Navigate calendar when initialDate prop changes (e.g., clicking a different notification)
  useEffect(() => {
    if (initialDate) {
      const [y, m, d] = initialDate.split('-').map(Number)
      if (y && m && d) {
        setCurrentDate(new Date(y, m - 1, d))
      }
    }
  }, [initialDate])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<Lesson | null>(null)
  const [showCreatePanel, setShowCreatePanel] = useState(false)
  const [clickedDate, setClickedDate] = useState<Date | null>(null)
  // Create form state for new lessons
  const [newLessonData, setNewLessonData] = useState({
    title: 'Piano Lesson',
    date: '',
    startTime: '',
    endTime: '',
    studentName: '',
    instrument: 'Piano',
    notes: '',
  })
  const createPanelRef = useRef<HTMLDivElement>(null)
  // Add state for tracking auth status
  const [authChecked, setAuthChecked] = useState(false)
  // Add state for network status
  const [isOffline, setIsOffline] = useState(false)
  // Add state for tracking validation errors
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  // Add state for security status
  const [securityStatus, setSecurityStatus] = useState<{
    hasAccess: boolean
    accessLevel: 'none' | 'read' | 'write'
    lastChecked: Date | null
  }>({
    hasAccess: false,
    accessLevel: 'none',
    lastChecked: null,
  })
  // Add state for month-year picker modal
  const [showMonthYearPickerModal, setShowMonthYearPickerModal] =
    useState(false)
  const [tempYearMonth, setTempYearMonth] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
  })
  // Add state for month-year picker
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false)
  const monthYearButtonRef = useRef<HTMLButtonElement>(null)
  // Add state for modal visibility
  const [showOptionsModal, setShowOptionsModal] = useState(false)
  const [showLessonModal, setShowLessonModal] = useState(false)
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false)
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  // Add new state for popover positioning and visibility
  const [popoverEvent, setPopoverEvent] = useState<Lesson | null>(null)
  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
  })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 })
  const popoverRef = useRef<HTMLDivElement>(null)
  // Add new state for the full modal
  const [showViewLessonModal, setShowViewLessonModal] = useState(false)
  // Add new state for the lesson being viewed in the full modal
  const [viewingLesson, setViewingLesson] = useState<Lesson | null>(null)
  // Add state for duplicate lesson modal
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
  const [lessonToDuplicate, setLessonToDuplicate] = useState<Lesson | null>(
    null,
  )
  const [duplicateDate, setDuplicateDate] = useState('')
  const [duplicateTime, setDuplicateTime] = useState('')
  const [isDuplicating, setIsDuplicating] = useState(false)
  // Add state for recurring lesson delete modal
  const [isRecurringDeleteModalOpen, setIsRecurringDeleteModalOpen] =
    useState(false)
  const [lessonToDelete, setLessonToDelete] = useState<Lesson | null>(null)
  const [recurringDeleteScope, setRecurringDeleteScope] = useState<
    'this' | 'future' | 'all'
  >('this')
  const [recurringDeleteCount, setRecurringDeleteCount] = useState(1)
  const [isRecurringDeleting, setIsRecurringDeleting] = useState(false)
  // Add state for mobile view menu
  const [showMobileViewMenu, setShowMobileViewMenu] = useState(false)
  const mobileViewMenuRef = useRef<HTMLDivElement>(null)
  // Calculate date range based on current view
  const dateRange =
    view === 'month'
      ? getDateRangeForMonth(currentDate)
      : view === 'week'
        ? getDateRangeForWeek(currentDate)
        : getDateRangeForDay(currentDate)
  console.log('🔍 [FILTERING/RANGE] Calendar date range:', {
    view,
    currentDate: currentDate.toISOString(),
    fromISOUtc: dateRange.fromISOUtc,
    toISOUtc: dateRange.toISOUtc,
    currentDateLocal: currentDate.toLocaleDateString('en-US', {
      timeZone: DEFAULT_TIMEZONE,
    }),
  })
  // Use our new lessons hook with the current date range
  const { lessons, byDay, visible, createLesson, deleteLesson, updateLesson } =
    useLessons({
      teacherId: currentUser?.uid || '',
      tz: DEFAULT_TIMEZONE,
      range: dateRange,
    })
  console.log('🔍 [FILTERING/RANGE] Hook returned:', {
    lessonsCount: lessons.length,
    byDaySize: byDay.size,
    visibleCount: visible.length,
    byDayKeys: Array.from(byDay.keys()).sort(),
  })
  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setMonth(prev.getMonth() - 1)
      return newDate
    })
  }
  const goToNextMonth = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setMonth(prev.getMonth() + 1)
      return newDate
    })
  }
  const goToPreviousWeek = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() - 7)
      return newDate
    })
  }
  const goToNextWeek = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() + 7)
      return newDate
    })
  }
  const goToPreviousDay = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() - 1)
      return newDate
    })
  }
  const goToNextDay = () => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev)
      newDate.setDate(prev.getDate() + 1)
      return newDate
    })
  }
  // MOVED INSIDE COMPONENT: Handle reschedule from Lesson Details Modal
  const handleRescheduleLesson = (lesson: Lesson) => {
    console.log('[CALENDAR] Initiating reschedule for lesson:', lesson.id)
    // Convert UTC times to local for form prefill
    const startLocal = toLocal(lesson.startAt, DEFAULT_TIMEZONE)
    const endLocal = toLocal(lesson.endAt, DEFAULT_TIMEZONE)
    // Format date and times for form
    const dateStr = formatLocalDate(startLocal)
    const startTime = `${startLocal.getHours().toString().padStart(2, '0')}:${startLocal.getMinutes().toString().padStart(2, '0')}`
    const endTime = `${endLocal.getHours().toString().padStart(2, '0')}:${endLocal.getMinutes().toString().padStart(2, '0')}`
    // Prefill form with lesson data
    setNewLessonData({
      title: lesson.title || 'Piano Lesson',
      date: dateStr,
      startTime: startTime,
      endTime: endTime,
      studentName: lesson.studentName || '',
      instrument: lesson.instrument || 'Piano',
      notes: lesson.notes || '',
    })
    // Set reschedule mode
    setIsRescheduleMode(true)
    setSourceLessonId(lesson.id)
    // Open the create panel
    setShowCreatePanel(true)
    // Close the lesson details modal
    setShowViewLessonModal(false)
    setViewingLesson(null)
  }
  // Add network status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    // Set initial state
    setIsOffline(!navigator.onLine)
    // Add event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])
  // Persist view preference to localStorage
  useEffect(() => {
    localStorage.setItem('calendar-view', view)
  }, [view])
  // Add security check effect - This runs on component mount and when auth state changes
  useEffect(() => {
    const checkSecurity = () => {
      if (authLoading) return
      const hasAccess = SecurityService.canAccessTeacherCalendar(
        currentUser,
        userType,
      )
      const canModify = currentUser
        ? SecurityService.canModifyCalendar(
            currentUser,
            userType,
            currentUser.uid,
          )
        : false
      setSecurityStatus({
        hasAccess,
        accessLevel: canModify ? 'write' : hasAccess ? 'read' : 'none',
        lastChecked: new Date(),
      })
      if (!hasAccess) {
        setError('You do not have permission to access this calendar.')
        setErrorCode('security/access-denied')
      }
    }
    checkSecurity()
  }, [currentUser, userType, authLoading])
  // UPDATED: Handle creating or updating a lesson
  const handleCreateLesson = async (lessonData: any) => {
    try {
      // ENHANCED DEBUG: Log the input data
      console.log('[CALENDAR] handleCreateLesson called with:', {
        lessonData,
        date: lessonData.date,
        startTime: lessonData.startTime,
        endTime: lessonData.endTime,
      })
      // Security check
      if (securityStatus.accessLevel !== 'write') {
        throw new Error('You do not have permission to create lessons.')
      }
      if (!currentUser) {
        throw new Error('You must be logged in to create lessons.')
      }
      // Convert local date/time to UTC ISO
      const localStartISO = `${lessonData.date}T${lessonData.startTime}:00`
      const localEndISO = `${lessonData.date}T${lessonData.endTime || lessonData.startTime}:00`
      console.log('[CALENDAR] Local ISO strings:', {
        localStartISO,
        localEndISO,
        timezone: DEFAULT_TIMEZONE,
      })
      const startAtUtc = toUtcISOString(localStartISO, DEFAULT_TIMEZONE)
      const endAtUtc = toUtcISOString(localEndISO, DEFAULT_TIMEZONE)
      console.log('[CALENDAR] UTC ISO strings:', {
        startAtUtc,
        endAtUtc,
      })
      // Validate the range
      validateRange(startAtUtc, endAtUtc)
      // Convert UTC ISO strings to Date objects for createLesson
      const startAtDate = new Date(startAtUtc)
      const endAtDate = new Date(endAtUtc)
      console.log('[CALENDAR] Converted to Date objects:', {
        startAtDate,
        startAtDateISO: startAtDate.toISOString(),
        startAtDateLocal: startAtDate.toLocaleString('en-US', {
          timeZone: DEFAULT_TIMEZONE,
        }),
        endAtDate,
        endAtDateISO: endAtDate.toISOString(),
        endAtDateLocal: endAtDate.toLocaleString('en-US', {
          timeZone: DEFAULT_TIMEZONE,
        }),
      })
      // Prepare lesson data with Date objects
      const newLesson = {
        title: lessonData.title,
        startAt: startAtDate,
        durationMin: Math.round(
          (endAtDate.getTime() - startAtDate.getTime()) / 60000,
        ),
        timezone: DEFAULT_TIMEZONE,
        teacherId: currentUser.uid,
        studentId: lessonData.studentId || null,
        instrument: lessonData.instrument,
        studentName: lessonData.studentName,
        notes: lessonData.notes,
        color: getInstrumentColor(lessonData.instrument),
        status: 'SCHEDULED',
        location: lessonData.location || null,
        lessonType: lessonData.lessonType || null,
      }
      console.log('[CALENDAR] Prepared lesson data:', {
        ...newLesson,
        startAt: newLesson.startAt.toISOString(),
        startAtLocal: newLesson.startAt.toLocaleString('en-US', {
          timeZone: DEFAULT_TIMEZONE,
        }),
      })
      // Validate and sanitize
      const sanitizedData = SecurityService.sanitizeLessonData(newLesson)
      const validation = SecurityService.validateLessonData({
        ...sanitizedData,
        startAt: sanitizedData.startAt.toISOString(),
        endAt: new Date(
          sanitizedData.startAt.getTime() + sanitizedData.durationMin * 60000,
        ).toISOString(),
      })
      if (!validation.isValid) {
        setValidationErrors(validation.errors)
        throw new Error(`Invalid lesson data: ${validation.errors.join(', ')}`)
      }
      setValidationErrors([])
      // Create the lesson using our hook (which handles optimistic updates)
      const id = await createLesson(sanitizedData)
      console.log('[CALENDAR] Lesson created with ID:', id)
      // Log what date key this lesson should generate
      const expectedDateKey = ymdLocal(startAtUtc, DEFAULT_TIMEZONE)
      console.log(
        '[CALENDAR] Expected date key for new lesson:',
        expectedDateKey,
      )
      return id
    } catch (err) {
      console.error('Error creating/updating lesson:', err)
      throw err
    }
  }
  // UPDATED: Use a separate effect to wait for auth to resolve before checking access
  useEffect(() => {
    if (devMode) {
      console.log(
        'TeacherCalendar: Development mode active, bypassing auth checks',
      )
      setAuthChecked(true)
      setSecurityStatus({
        hasAccess: true,
        accessLevel: 'write',
        lastChecked: new Date(),
      })
      setLoading(false)
      return
    }
    if (!authLoading) {
      setAuthChecked(true)
      if (currentUser && userType === 'teacher') {
        setLoading(false)
      } else if (currentUser && userType !== 'teacher') {
        setLoading(false)
        setError('This calendar is only available to teacher accounts.')
        setErrorCode('auth/wrong-account-type')
      } else if (!currentUser) {
        setLoading(false)
        setError('You need to be logged in to access your calendar.')
        setErrorCode('auth/not-logged-in')
      }
    }
  }, [currentUser, userType, authLoading, isOffline, devMode])
  // Add a retry function for error states
  const handleRetry = () => {
    setLoading(true)
    setError(null)
    setErrorCode(null)
    setTimeout(() => {
      if (!currentUser) {
        setError('You need to be logged in to access your calendar.')
        setErrorCode('auth/not-logged-in')
      } else if (userType !== 'teacher') {
        setError('This calendar is only available to teacher accounts.')
        setErrorCode('auth/wrong-account-type')
      }
      setLoading(false)
    }, 1000)
  }
  // Add a function to render the security status indicator
  const renderSecurityStatus = () => {
    if (!securityStatus.lastChecked) return null
    let statusIcon
    let statusText = ''
    let statusClass = ''
    switch (securityStatus.accessLevel) {
      case 'write':
        statusIcon = <CheckCircleIcon className="h-4 w-4 text-green-500" />
        statusText = 'Full Access'
        statusClass = 'bg-green-500/20 text-green-600'
        break
      case 'read':
        statusIcon = <ShieldIcon className="h-4 w-4 text-yellow-500" />
        statusText = 'Read Only'
        statusClass = 'bg-yellow-500/20 text-yellow-600'
        break
      case 'none':
        statusIcon = <ShieldAlertIcon className="h-4 w-4 text-red-500" />
        statusText = 'No Access'
        statusClass = 'bg-red-500/20 text-red-600'
        break
    }
    return (
      <div
        className={`${statusClass} text-xs px-2 py-1 rounded-full flex items-center gap-1 ml-2`}
      >
        {statusIcon}
        <span>{statusText}</span>
      </div>
    )
  }
  // Add a loading state
  const renderLoadingState = () => {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoaderIcon className="h-12 w-12 text-[#DC2626] animate-spin mb-4" />
        <p className={`${isDark ? 'text-white' : 'text-gray-800'} text-lg`}>
          Loading your calendar...
        </p>
      </div>
    )
  }
  // Add function to render auth error states with better visuals based on error code
  const renderAuthError = () => {
    let errorTitle = 'Calendar Access Error'
    let errorMessage = error || 'An unexpected error occurred.'
    let errorIcon = <AlertCircleIcon className="h-16 w-16 text-red-500 mb-4" />
    let actionButton = null
    if (errorCode === 'auth/wrong-account-type') {
      errorTitle = 'Teacher Access Required'
      errorMessage =
        "The calendar feature is only available to teacher accounts. If you're a teacher, please make sure your account is properly configured."
      errorIcon = <UserIcon className="h-16 w-16 text-yellow-500 mb-4" />
      actionButton = (
        <button
          onClick={() => (window.location.href = '/user-info')}
          className="bg-[#DC2626] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors mt-4"
        >
          Update Profile
        </button>
      )
    } else if (errorCode === 'auth/not-logged-in') {
      errorTitle = 'Authentication Required'
      errorMessage = 'You need to be logged in to access your calendar.'
      errorIcon = <LockIcon className="h-16 w-16 text-yellow-500 mb-4" />
      actionButton = (
        <button
          onClick={() => (window.location.href = '/teacher-login')}
          className="bg-[#DC2626] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors mt-4"
        >
          Log In
        </button>
      )
    } else if (
      errorCode === 'network/offline' ||
      errorCode === 'network/unavailable'
    ) {
      errorTitle = 'Network Error'
      errorMessage =
        'Unable to connect to our servers. Please check your internet connection and try again.'
      errorIcon = <WifiOffIcon className="h-16 w-16 text-yellow-500 mb-4" />
      actionButton = (
        <button
          onClick={handleRetry}
          className="bg-[#DC2626] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors mt-4 flex items-center"
        >
          <RefreshCwIcon className="h-4 w-4 mr-2" />
          Retry Connection
        </button>
      )
    } else if (errorCode === 'security/access-denied') {
      errorTitle = 'Access Denied'
      errorMessage =
        "You don't have permission to access this calendar. Please contact support if you believe this is an error."
      errorIcon = <UserXIcon className="h-16 w-16 text-red-500 mb-4" />
      actionButton = (
        <button
          onClick={() => (window.location.href = '/user-info')}
          className="bg-[#DC2626] hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors mt-4"
        >
          View Account Status
        </button>
      )
    }
    return (
      <div
        className={`${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl p-8 text-center`}
      >
        <div className="flex flex-col items-center justify-center py-8">
          {errorIcon}
          <h3
            className={`${isDark ? 'text-white' : 'text-gray-800'} text-xl font-medium mb-2`}
          >
            {errorTitle}
          </h3>
          <p
            className={`${isDark ? 'text-gray-300' : 'text-gray-600'} max-w-md mb-2`}
          >
            {errorMessage}
          </p>
          {actionButton}
          <button
            onClick={() => window.location.reload()}
            className={`${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'} px-4 py-2 rounded-lg transition-colors mt-4`}
          >
            <RefreshCwIcon className="h-4 w-4 mr-2 inline" />
            Refresh Page
          </button>
        </div>
      </div>
    )
  }
  // Add a component to render validation errors
  const renderValidationErrors = () => {
    if (validationErrors.length === 0) return null
    return (
      <div className="mt-4 bg-red-500/20 border border-red-500/30 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h4
              className={`${isDark ? 'text-white' : 'text-gray-800'} font-medium mb-1`}
            >
              Validation Errors
            </h4>
            <ul
              className={`${isDark ? 'text-gray-300' : 'text-gray-600'} list-disc list-inside space-y-1`}
            >
              {validationErrors.map((er, index) => (
                <li key={index}>{er}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    )
  }
  // Enhanced empty state for no events
  const renderNoEventsMessage = () => {
    return null // Return null instead of the message section
  }
  // Add a security info panel
  const renderSecurityInfoPanel = () => {
    return (
      <div
        className={`mt-4 p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
      >
        <div className="flex items-start">
          <KeyIcon
            className={`h-5 w-5 ${isDark ? 'text-white' : 'text-gray-700'} mr-3 flex-shrink-0 mt-0.5`}
          />
          <div>
            <h4
              className={`${isDark ? 'text-white' : 'text-gray-800'} font-medium mb-1 flex items-center`}
            >
              Security Status
              {renderSecurityStatus()}
            </h4>
            <p
              className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-sm`}
            >
              {securityStatus.accessLevel === 'write'
                ? 'You have full access to create, edit, and delete lessons in this calendar.'
                : securityStatus.accessLevel === 'read'
                  ? 'You have read-only access to this calendar. You cannot make changes.'
                  : 'You do not have access to this calendar. Please contact support if you believe this is an error.'}
            </p>
            <p
              className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-xs mt-1`}
            >
              Last verified:{' '}
              {securityStatus.lastChecked?.toLocaleTimeString() || 'Never'}
            </p>
          </div>
        </div>
      </div>
    )
  }
  // Add debug panel for development
  const renderDebugPanel = () => {
    return DEBUG_CALENDAR ? (
      <div
        className={`mt-4 p-4 ${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-lg border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
      >
        <h4
          className={`${isDark ? 'text-white' : 'text-gray-800'} font-medium mb-1`}
        >
          Debug Info
        </h4>
        <div
          className={`${isDark ? 'text-gray-300' : 'text-gray-600'} text-xs`}
        >
          <p>
            Auth State:{' '}
            {authLoading
              ? 'Loading'
              : currentUser
                ? 'Authenticated'
                : 'Not Authenticated'}
          </p>
          <p>User Type: {userType || 'None'}</p>
          <p>View: {view}</p>
          <p>Timezone: {DEFAULT_TIMEZONE}</p>
          <p>Total Events: {lessons.length}</p>
          <p>
            Date Range: {dateRange.fromISOUtc} to {dateRange.toISOUtc}
          </p>
          <p>Days with Events: {byDay.size}</p>
          <div className="flex space-x-2 mt-2">
            <button
              onClick={() => {
                console.log('[CALENDAR] All lessons:', lessons)
                console.log('[CALENDAR] Events by day:', byDay)
              }}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs"
            >
              Log Events
            </button>
          </div>
        </div>
      </div>
    ) : null
  }
  // Add debug overlay component
  const renderDebugOverlay = () => {
    // ONLY render the overlay when DEBUG_CALENDAR is true
    if (!DEBUG_CALENDAR) return null
    console.log('Overlay render tick', new Date().toISOString())
    // Format date to local YYYY-MM-DD format
    const formatLocalDateForDebug = (dateStr: string): string => {
      try {
        const localDate = toLocal(dateStr, DEFAULT_TIMEZONE)
        return `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`
      } catch (e) {
        return 'Invalid date'
      }
    }
    // Format time to local HH:MM format
    const formatLocalTimeForDebug = (dateStr: string): string => {
      try {
        const localDate = toLocal(dateStr, DEFAULT_TIMEZONE)
        return `${String(localDate.getHours()).padStart(2, '0')}:${String(localDate.getMinutes()).padStart(2, '0')}`
      } catch (e) {
        return 'Invalid time'
      }
    }
    const currentTime = new Date().toLocaleTimeString()
    return (
      <div className="mb-4 p-3 bg-red-500/20 border-2 border-red-500 rounded-lg text-xs overflow-auto">
        <div className="font-bold mb-1 text-red-500 text-sm">
          DEBUG OVERLAY ACTIVE - {currentTime}
        </div>
        <div className="mb-1">
          <span className="font-semibold">Calendar Range:</span>{' '}
          {dateRange.fromISOUtc} to {dateRange.toISOUtc}
        </div>
        <div className="mb-1">
          <span className="font-semibold">Total Lessons in Memory:</span>{' '}
          {lessons.length}
        </div>
        {lessons.length > 0 && (
          <div>
            <div className="font-semibold mb-1">
              First {Math.min(10, lessons.length)} Lessons:
            </div>
            <div className="max-h-40 overflow-y-auto pl-2 border-l-2 border-red-400">
              {lessons.slice(0, 10).map((lesson) => (
                <div key={lesson.id} className="mb-1 break-all">
                  {formatLocalDateForDebug(lesson.startAt)} —{' '}
                  {formatLocalTimeForDebug(lesson.startAt)} — {lesson.title} — [
                  {lesson.id.substring(0, 6)}...]
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }
  // IMPROVED: Function to handle lesson chip click with better positioning
  const handleLessonClick = (event: Lesson, e: React.MouseEvent) => {
    e.stopPropagation()
    const popoverWidth = 320 // Width of popover (w-80 = 320px)
    const popoverHeight = 400 // Estimated height of popover
    // Get viewport dimensions
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    // Calculate centered position
    let left = (viewportWidth - popoverWidth) / 2
    let top = (viewportHeight - popoverHeight) / 2
    // Ensure popover stays within viewport bounds with padding
    const padding = 20
    left = Math.max(
      padding,
      Math.min(left, viewportWidth - popoverWidth - padding),
    )
    top = Math.max(
      padding,
      Math.min(top, viewportHeight - popoverHeight - padding),
    )
    // FIXED: Removed scroll offset additions for fixed positioning
    // position: fixed uses viewport-relative coordinates, not document-relative
    // Adding window.scrollY/scrollX was causing double-counting of scroll position
    setPopoverPosition({ top, left })
    setPopoverEvent(event)
    setIsDragging(false)
    setDragOffset({ x: 0, y: 0 })
  }
  // NEW: Handle drag start
  const handleDragStart = (e: React.MouseEvent) => {
    if (!popoverRef.current) return
    setIsDragging(true)
    setDragStartPos({
      x: e.clientX,
      y: e.clientY,
    })
  }
  // NEW: Handle dragging
  const handleDrag = (e: MouseEvent) => {
    if (!isDragging) return
    const deltaX = e.clientX - dragStartPos.x
    const deltaY = e.clientY - dragStartPos.y
    setDragOffset({ x: deltaX, y: deltaY })
  }
  // NEW: Handle drag end
  const handleDragEnd = () => {
    if (!isDragging) return
    // Update the base position with the drag offset
    setPopoverPosition({
      top: popoverPosition.top + dragOffset.y,
      left: popoverPosition.left + dragOffset.x,
    })
    setIsDragging(false)
    setDragOffset({ x: 0, y: 0 })
  }
  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag)
      window.addEventListener('mouseup', handleDragEnd)
      return () => {
        window.removeEventListener('mousemove', handleDrag)
        window.removeEventListener('mouseup', handleDragEnd)
      }
    }
  }, [isDragging, dragStartPos, dragOffset])
  // Function to check if a date is in the past
  const isDateInPast = (date: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }
  // Add function to close the popover
  const closePopover = () => {
    setPopoverEvent(null)
    setPopoverPosition({ top: 0, left: 0 })
  }
  // Add click-outside detection for popover
  useEffect(() => {
    if (!popoverEvent) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        // CRITICAL FIX: Check if click is on a calendar cell or time slot
        // If yes, let the cell/slot's onClick handler deal with it (don't close popover here)
        const clickedElement = target as Element
        if (clickedElement.closest) {
          // Check for Month view calendar cells
          const isCalendarCell = clickedElement.closest('.calendar-cell')
          // Check for Week view time slots
          const isTimeSlot = clickedElement.closest('.calendar-time-slot')
          // Check for Day view time slots
          const isDaySlot = clickedElement.closest('.calendar-day-slot')
          if (isCalendarCell || isTimeSlot || isDaySlot) {
            // Click is on a calendar cell or time slot - let handler deal with it
            // This prevents race condition where click-outside closes popover
            // and sets popoverEvent = null before onClick can check the guard
            return
          }
        }
        // True outside click (not on popover, not on calendar cell/slot)
        closePopover()
      }
    }
    // Add a small delay to prevent immediate closure from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [popoverEvent])
  // Add click-outside detection for mobile view menu
  useEffect(() => {
    if (!showMobileViewMenu) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mobileViewMenuRef.current &&
        !mobileViewMenuRef.current.contains(e.target as Node)
      ) {
        setShowMobileViewMenu(false)
      }
    }
    // Add a small delay to prevent immediate closure from the same click that opened it
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)
    return () => {
      clearTimeout(timeoutId)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMobileViewMenu])
  // Handler for month/year picker selection
  const handleMonthYearSelect = (year: number, month: number) => {
    console.log('🔴 [PICKER CALLBACK] handleMonthYearSelect CALLED')
    console.log('🔴 [PICKER CALLBACK] Received:', {
      year,
      month,
      monthName: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ][month],
    })
    console.log('🔴 [PICKER CALLBACK] Current state before update:', {
      currentDate: currentDate.toISOString(),
      currentMonth: currentDate.getMonth(),
      currentYear: currentDate.getFullYear(),
      view,
    })
    const newDate = new Date(year, month, 1)
    console.log('🔴 [PICKER CALLBACK] New date created:', {
      newDate: newDate.toISOString(),
      newMonth: newDate.getMonth(),
      newYear: newDate.getFullYear(),
    })
    setCurrentDate(newDate)
    console.log(
      '🔴 [PICKER CALLBACK] setCurrentDate called with:',
      newDate.toISOString(),
    )
    setShowMonthYearPicker(false)
    console.log('🔴 [PICKER CALLBACK] Picker closed')
    // Switch to month view when selecting a new month/year
    if (view !== 'month') {
      setView('month')
      console.log('🔴 [PICKER CALLBACK] Switched view to month')
    }
    console.log('🔴 [PICKER CALLBACK] Handler complete')
  }
  // Detect if mobile (for picker variant)
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  // Month view - update to make debug indicators more visible
  const renderMonthView = () => {
    console.log('🔍 [RENDER PIPE] renderMonthView called')
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    const firstDayOfMonth = getFirstDayOfMonth(year, month)
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    // Add console log to check lessons being received
    console.log('Calendar rendering lessons:', lessons)
    console.log('Calendar rendering lessons by day:', byDay)
    console.log('Current date range:', dateRange)
    // PRIORITY 1: Log first 3 grouping keys from byDay
    const groupingKeys = Array.from(byDay.keys()).sort()
    console.log(
      '🔑 [KEY MATCH CHECK] First 3 grouping keys from byDay:',
      groupingKeys.slice(0, 3),
    )
    // Always log debug info
    console.log(
      `[CALENDAR] Rendering month view for ${monthNames[month]} ${year} with ${lessons.length} total events`,
    )
    console.log('🔍 [RENDER PIPE] Month view data:', {
      year,
      month: month + 1,
      daysInMonth,
      firstDayOfMonth,
      lessonsCount: lessons.length,
      byDaySize: byDay.size,
      byDayKeys: Array.from(byDay.keys()).sort(),
    })
    const totalCells = Math.ceil((firstDayOfMonth + daysInMonth) / 7) * 7
    const cells: JSX.Element[] = []
    // PRIORITY 1: Track first 3 render keys for comparison
    const renderKeys: string[] = []
    const monthHeader = (
      <div className="flex flex-col mb-4">
        {/* Mobile: Today button + centered month/year */}
        <div className="md:hidden mb-3">
          <div className="flex items-center justify-between">
            {/* Mobile Today button - left side */}
            <button
              onClick={() => setCurrentDate(new Date())}
              className={`px-3 py-1.5 text-sm font-medium transition-colors bg-transparent text-[#10B981] border-b-2 border-[#10B981]`}
            >
              Today
            </button>
            {/* Spacer for balance */}
            <div className="w-[60px]"></div>
          </div>
          {/* Centered month/year navigation */}
          <div className="flex items-center justify-center mt-2">
            <button
              onClick={view === 'month' ? goToPreviousMonth : goToPreviousWeek}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
              aria-label={view === 'month' ? 'Previous month' : 'Previous week'}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
            <div className="relative">
              <button
                ref={monthYearButtonRef}
                onClick={(e) => {
                  e.stopPropagation()
                  console.log('🔴 [PICKER TRIGGER] Month/Year button clicked')
                  console.log('🔴 [PICKER TRIGGER] Current state:', {
                    showMonthYearPicker,
                    willToggleTo: !showMonthYearPicker,
                  })
                  setShowMonthYearPicker(!showMonthYearPicker)
                  console.log(
                    '🔴 [PICKER TRIGGER] setShowMonthYearPicker called with:',
                    !showMonthYearPicker,
                  )
                }}
                className={`flex items-center text-lg font-medium mx-2 ${isDark ? 'text-white hover:text-gray-200' : 'text-gray-800 hover:text-gray-600'} transition-colors cursor-pointer`}
                aria-label="Select month and year"
              >
                <span>
                  {view === 'month'
                    ? `${monthNames[month]} ${year}`
                    : `Week of ${new Date(dateRange.fromISOUtc).toLocaleDateString()}`}
                </span>
                <ChevronDownIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
            <button
              onClick={view === 'month' ? goToNextMonth : goToNextWeek}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
              aria-label={view === 'month' ? 'Next month' : 'Next week'}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Desktop layout - unchanged */}
        <div className="hidden md:flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {/* Desktop Today button */}
            <button
              onClick={() => setCurrentDate(new Date())}
              className={`px-4 py-2 rounded-md text-sm border-2 transition-colors ${
                isDark
                  ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10'
                  : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'
              }`}
            >
              Today
            </button>
            <div className="flex items-center">
              <button
                onClick={
                  view === 'month' ? goToPreviousMonth : goToPreviousWeek
                }
                className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                aria-label={
                  view === 'month' ? 'Previous month' : 'Previous week'
                }
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              {/* Clickable month/year - opens picker */}
              <div className="relative">
                <button
                  ref={monthYearButtonRef}
                  onClick={(e) => {
                    e.stopPropagation()
                    console.log('🔴 [PICKER TRIGGER] Month/Year button clicked')
                    console.log('🔴 [PICKER TRIGGER] Current state:', {
                      showMonthYearPicker,
                      willToggleTo: !showMonthYearPicker,
                    })
                    setShowMonthYearPicker(!showMonthYearPicker)
                    console.log(
                      '🔴 [PICKER TRIGGER] setShowMonthYearPicker called with:',
                      !showMonthYearPicker,
                    )
                  }}
                  className={`flex items-center text-lg font-medium mx-2 ${isDark ? 'text-white hover:text-gray-200' : 'text-gray-800 hover:text-gray-600'} transition-colors cursor-pointer`}
                  aria-label="Select month and year"
                >
                  <span>
                    {view === 'month'
                      ? `${monthNames[month]} ${year}`
                      : `Week of ${new Date(dateRange.fromISOUtc).toLocaleDateString()}`}
                  </span>
                  <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
              <button
                onClick={view === 'month' ? goToNextMonth : goToNextWeek}
                className={`p-2 rounded-full ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
                aria-label={view === 'month' ? 'Next month' : 'Next week'}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          {/* Desktop view toggle */}
          <div className="flex gap-2">
            {/* View selector */}
            <CalendarViewSelector
              currentView={view}
              onViewChange={setView}
              isDark={isDark}
              availableViews={['day', 'week', 'month']}
            />
          </div>
        </div>
      </div>
    )
    let renderedChipsCount = 0
    for (let i = 0; i < totalCells; i++) {
      const dayIndex = i - firstDayOfMonth + 1
      if (dayIndex > 0 && dayIndex <= daysInMonth) {
        // PRIORITY 1: Generate date key using same format as grouping
        // Instead of formatLocalDate(date) which uses browser timezone,
        // directly construct YYYY-MM-DD from calendar coordinates
        const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(dayIndex).padStart(2, '0')}`
        // Track first 3 render keys
        if (renderKeys.length < 3) {
          renderKeys.push(dateString)
        }
        const date = new Date(year, month, dayIndex)
        const dateEvents = byDay.get(dateString) || []
        console.log('🔍 [RENDER PIPE] Cell generation:', {
          cellIndex: i,
          dayIndex,
          dateString,
          dateISO: date.toISOString(),
          eventsFound: dateEvents.length,
          eventIds: dateEvents.map((e) => e.id),
        })
        if (dateEvents.length > 0) {
          console.log(
            '🔍 [RENDER PIPE] Events for',
            dateString,
            ':',
            dateEvents.map((e) => ({
              id: e.id,
              startAt: e.startAt,
              startAtType: typeof e.startAt,
              title: e.title,
            })),
          )
        }
        const isCurrentDay = isToday(date)
        const maxEventsToShow = 3
        const eventsToShow = dateEvents.slice(0, maxEventsToShow)
        const overflowCount = Math.max(0, dateEvents.length - maxEventsToShow)
        renderedChipsCount += eventsToShow.length
        cells.push(
          <div
            key={i}
            className={`min-h-[100px] p-2 ${isDark ? 'bg-navy-800 hover:bg-navy-700' : 'bg-white hover:bg-gray-50'} 
                       ${i % 7 === 0 || i % 7 === 6 ? (isDark ? 'bg-navy-800/80' : 'bg-gray-50/50') : ''} 
                       transition-colors cursor-pointer relative calendar-cell hover:shadow-[0_0_0_1px_rgba(230,57,70,0.3)] hover:z-10`}
            onClick={(e) => {
              // CRITICAL: Check popover state FIRST, before any other checks
              // If lesson popover is open, close it and don't trigger any other actions
              if (popoverEvent) {
                closePopover()
                return
              }
              const target = e.target as HTMLElement
              const isLessonChip = target.closest('.lesson-chip-wrapper')
              // Only trigger cell click if no lesson chip was clicked
              if (!isLessonChip && onCellClick) {
                onCellClick(date)
              }
            }}
            data-date={dateString}
          >
            <div className="flex justify-between items-center mb-1">
              <span
                className={`text-sm font-medium ${isCurrentDay ? 'text-deep-pink' : isDark ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {dayIndex}
              </span>
            </div>
            {/* Event chips */}
            <div className="space-y-1 mt-1 relative z-10">
              {eventsToShow.map((event, index) => {
                console.log('🔍 [RENDER PIPE] Rendering chip for:', {
                  id: event.id,
                  startAt: event.startAt,
                  startAtType: typeof event.startAt,
                })
                return (
                  <div key={event.id} className="w-full">
                    {renderLessonChip(event, true, index)}
                  </div>
                )
              })}
              {overflowCount > 0 && (
                <div
                  className={`text-xs p-1 text-center rounded-lg ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} cursor-pointer`}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  +{overflowCount} more
                </div>
              )}
            </div>
          </div>,
        )
      } else {
        // Previous or next month days (empty cells)
        cells.push(
          <div
            key={i}
            className={`min-h-[100px] p-2 ${isDark ? 'bg-navy-800/50 text-gray-600' : 'bg-gray-100 text-gray-400'} calendar-cell`}
          >
            {dayIndex <= 0
              ? getDaysInMonth(year, month - 1) + dayIndex
              : dayIndex - daysInMonth}
          </div>,
        )
      }
    }
    // PRIORITY 1: Log comparison of grouping keys vs render keys
    console.log('🔑 [KEY MATCH CHECK] Comparison:', {
      groupingKeys: groupingKeys.slice(0, 3),
      renderKeys: renderKeys.slice(0, 3),
      keysMatch: groupingKeys
        .slice(0, 3)
        .every((key, i) => key === renderKeys[i]),
    })
    // PROOF LOG: Chip render count
    console.log('✅ [FIX 3 PROOF] Chip render count:', {
      renderedChipsCount,
      totalLessons: lessons.length,
      daysWithLessons: byDay.size,
    })
    console.log('🔍 [RENDER PIPE] Month view rendering complete:', {
      totalCells: cells.length,
      renderedChipsCount,
      expectedChipsCount: Array.from(byDay.values()).reduce(
        (sum, events) => sum + Math.min(events.length, 3),
        0,
      ),
    })
    return (
      <div data-testid="cal-month-view">
        {monthHeader}
        {/* Single shared MonthYearPicker instance - renders once for both mobile and desktop */}
        {showMonthYearPicker && view === 'month' && (
          <MonthYearPicker
            currentYear={year}
            currentMonth={month}
            onSelect={handleMonthYearSelect}
            onClose={() => {
              console.log('🔴 [PICKER] onClose called - picker closing')
              setShowMonthYearPicker(false)
            }}
            isDark={isDark}
            isMobile={isMobile}
            triggerRef={monthYearButtonRef}
          />
        )}
        <div
          className={`rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`}
        >
          <div className="grid grid-cols-7 gap-px">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className={`py-2 text-center text-sm font-medium ${isDark ? 'bg-navy-900 text-white' : 'bg-gray-100 text-gray-700'}`}
              >
                {day}
              </div>
            ))}
            {cells}
          </div>
        </div>
        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-4">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-[#10B981] mr-2"></div>
            <span
              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
            >
              Confirmed
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-[#3B82F6] mr-2"></div>
            <span
              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
            >
              Completed
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-[#EAB308] mr-2"></div>
            <span
              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
            >
              Pending
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-[#EF4444] mr-2"></div>
            <span
              className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
            >
              Canceled
            </span>
          </div>
        </div>
      </div>
    )
  }
  // Render the week view using the TeacherCalendarWeek component
  const renderWeekView = () => {
    const weekRange = getDateRangeForWeek(currentDate)
    return (
      <div data-testid="cal-week-view">
        <TeacherCalendarWeek
          weekDays={weekRange.weekDays}
          lessons={visible}
          isDark={isDark}
          onTimeSlotClick={
            onTimeSlotClick ||
            ((date, hour, minute) => {
              // Default handler if no callback provided
              console.log(
                `Time slot clicked: ${date.toDateString()} at ${hour}:${minute}`,
              )
            })
          }
          onLessonClick={handleLessonClick}
          startHour={6}
          endHour={22}
          onViewChange={(view) => setView(view)}
          onPrevWeek={goToPreviousWeek}
          onNextWeek={goToNextWeek}
          onTodayClick={() => setCurrentDate(new Date())}
          popoverEvent={popoverEvent}
          onClosePopover={closePopover}
        />
      </div>
    )
  }
  // Render the day view using the TeacherCalendarDay component
  const renderDayView = () => {
    return (
      <div data-testid="cal-day-view">
        <TeacherCalendarDay
          selectedDate={currentDate}
          lessons={visible}
          isDark={isDark}
          onTimeSlotClick={
            onTimeSlotClick ||
            ((date, hour, minute) => {
              // Default handler if no callback provided
              console.log(
                `Time slot clicked: ${date.toDateString()} at ${hour}:${minute}`,
              )
            })
          }
          onLessonClick={handleLessonClick}
          startHour={6}
          endHour={22}
          onViewChange={(view) => setView(view)}
          onPrevDay={goToPreviousDay}
          onNextDay={goToNextDay}
          onTodayClick={() => setCurrentDate(new Date())}
          popoverEvent={popoverEvent}
          onClosePopover={closePopover}
        />
      </div>
    )
  }
  // IMPROVED: Render lesson chip using the LessonChip component
  const renderLessonChip = (
    event: Lesson,
    compact: boolean = false,
    chipIndex?: number,
  ) => {
    console.log('🔍 [RENDER PIPE] renderLessonChip called for:', {
      id: event.id,
      startAt: event.startAt,
      startAtType: typeof event.startAt,
      endAt: event.endAt,
      timezone: event.timezone,
      chipIndex,
    })
    // Convert UTC time to local time for display
    let eventStartLocal: Date
    let eventEndLocal: Date
    try {
      eventStartLocal = toLocal(event.startAt, DEFAULT_TIMEZONE)
      eventEndLocal = toLocal(event.endAt, DEFAULT_TIMEZONE)
      console.log('🔍 [RENDER PIPE] Time conversion successful:', {
        id: event.id,
        startAtUTC: event.startAt,
        startAtLocal: eventStartLocal.toISOString(),
        startAtLocalDisplay: eventStartLocal.toLocaleString('en-US', {
          timeZone: DEFAULT_TIMEZONE,
        }),
      })
    } catch (error) {
      console.error('🔍 [RENDER PIPE] Time conversion failed:', {
        id: event.id,
        startAt: event.startAt,
        startAtType: typeof event.startAt,
        error,
      })
      return null
    }
    // Extract time in HH:MM format
    const startTime = `${eventStartLocal.getHours().toString().padStart(2, '0')}:${eventStartLocal.getMinutes().toString().padStart(2, '0')}`
    const endTime = `${eventEndLocal.getHours().toString().padStart(2, '0')}:${eventEndLocal.getMinutes().toString().padStart(2, '0')}`
    // UPDATED: Use index-based color if chipIndex is provided, otherwise fall back to instrument color
    const bgColor =
      chipIndex !== undefined
        ? getLessonChipColor(chipIndex)
        : event.color || getInstrumentColor(event.instrument || 'Other')
    // Check if this is a temporary lesson (optimistic update)
    const isTemporary = event.id.startsWith('temp_')
    console.log('🔍 [RENDER PIPE] Rendering LessonChip with:', {
      id: event.id,
      startTime,
      endTime,
      bgColor,
      isTemporary,
      chipIndex,
    })
    return (
      <LessonChip
        key={event.id}
        id={event.id}
        title={event.title || 'Lesson'}
        startTime={startTime}
        endTime={endTime}
        studentName={event.studentName || 'Student'}
        instrument={event.instrument}
        color={bgColor}
        status={event.status}
        variant={compact ? 'compact' : 'detailed'}
        onClick={(id, e) => handleLessonClick(event, e)}
        isTemporary={isTemporary}
      />
    )
  }
  // Add helper function to convert Lesson to LessonDetails format
  const convertLessonToDetails = (lesson: Lesson | null): any => {
    if (!lesson) return null
    // Helper to normalize any date value to ISO string
    const normalizeToISO = (dateValue: any): string => {
      // Handle null/undefined first
      if (!dateValue) {
        throw new Error('Date value is required but was null or undefined')
      }
      if (typeof dateValue === 'string') {
        return dateValue
      }
      // Check for Firestore Timestamp (has toDate method)
      if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
        return dateValue.toDate().toISOString()
      }
      // Verify it's actually a Date object before calling toISOString
      if (dateValue instanceof Date) {
        return dateValue.toISOString()
      }
      // If we get here, the data type is unexpected
      throw new Error(`Unexpected date value type: ${typeof dateValue}`)
    }
    // Normalize startAt to ISO string (handle string, Timestamp, or Date)
    const startAtISO = normalizeToISO(lesson.startAt)
    // Convert startAt to local time
    const startLocal = toLocal(startAtISO, DEFAULT_TIMEZONE)
    // Compute endAt if it doesn't exist
    let endAtUtc: string
    if (lesson.endAt) {
      // Normalize endAt to ISO string (handle string, Timestamp, or Date)
      endAtUtc = normalizeToISO(lesson.endAt)
    } else if (lesson.durationMin || lesson.durationMinutes) {
      // Compute endAt from startAt + duration
      const duration = lesson.durationMin || lesson.durationMinutes || 60
      const startAtDate = new Date(startAtISO)
      const endAtDate = new Date(startAtDate.getTime() + duration * 60000)
      endAtUtc = endAtDate.toISOString()
    } else {
      // Fallback: assume 1 hour duration
      const startAtDate = new Date(startAtISO)
      const endAtDate = new Date(startAtDate.getTime() + 60 * 60000)
      endAtUtc = endAtDate.toISOString()
    }
    // Convert endAt to local time
    const endLocal = toLocal(endAtUtc, DEFAULT_TIMEZONE)
    const startTime = `${startLocal.getHours().toString().padStart(2, '0')}:${startLocal.getMinutes().toString().padStart(2, '0')}`
    const endTime = `${endLocal.getHours().toString().padStart(2, '0')}:${endLocal.getMinutes().toString().padStart(2, '0')}`
    const dateStr = startLocal.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    })
    // Calculate duration in minutes
    const durationMinutes =
      lesson.durationMinutes ||
      lesson.durationMin ||
      Math.round(
        (new Date(endAtUtc).getTime() - new Date(startAtISO).getTime()) / 60000,
      )
    return {
      id: lesson.id,
      firestoreId: lesson.id,
      title: lesson.title || 'Lesson',
      studentName: lesson.studentName || 'Student',
      studentId: lesson.studentId,
      instrument: lesson.instrument || 'Music',
      date: dateStr,
      time: `${formatTime12h(startTime)}–${formatTime12h(endTime)}`,
      duration: `${durationMinutes} minutes`,
      location: 'Online',
      status: lesson.status || 'Confirmed',
      notes: lesson.notes || '',
    }
  }
  const handleDuplicateLesson = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!lessonToDuplicate || !duplicateDate || !currentUser?.uid) return
    setIsDuplicating(true)
    try {
      // Calculate duration
      const startLocal = toLocal(lessonToDuplicate.startAt, DEFAULT_TIMEZONE)
      const endLocal = toLocal(lessonToDuplicate.endAt, DEFAULT_TIMEZONE)
      const durationMin = Math.round(
        (endLocal.getTime() - startLocal.getTime()) / 60000,
      )
      // Parse new date and time
      const [year, month, day] = duplicateDate.split('-').map(Number)
      const [hours, minutes] = duplicateTime.split(':').map(Number)
      const newStartLocal = new Date(year, month - 1, day, hours, minutes)
      await createLesson({
        title: lessonToDuplicate.title || 'Lesson',
        studentId: lessonToDuplicate.studentId,
        studentName: lessonToDuplicate.studentName || 'Student',
        teacherId: currentUser.uid,
        startAt: newStartLocal,
        durationMin: durationMin > 0 ? durationMin : 60,
        location: lessonToDuplicate.location || 'Online',
        instrument: lessonToDuplicate.instrument || 'Music',
        notes: lessonToDuplicate.notes || '',
        timezone: DEFAULT_TIMEZONE,
        lessonType: lessonToDuplicate.lessonType || 'Private Lesson',
      })
      toast.success('Lesson duplicated successfully')
      setIsDuplicateModalOpen(false)
      setLessonToDuplicate(null)
    } catch (error) {
      console.error('Error duplicating lesson:', error)
      toast.error('Failed to duplicate lesson')
    } finally {
      setIsDuplicating(false)
    }
  }
  // Recurring lesson deletion helpers
  // FIX: Use single-field query + client-side date filter to avoid missing composite index error.
  // Firestore requires a composite index for recurringGroupId + startAt, which may not be deployed.
  // Fetching all lessons in the series and filtering client-side is safe (series are typically < 100 docs).
  const fetchRecurringDeleteCount = async (
    lesson: Lesson,
    scope: 'this' | 'future' | 'all',
  ): Promise<number> => {
    if (scope === 'this') return 1
    if (!currentUser?.uid) return 1
    const recurringGroupId = (lesson as any).recurringGroupId
    if (!recurringGroupId) return 1
    try {
      console.log(
        '[RECURRING DELETE] fetchCount: scope =',
        scope,
        ', recurringGroupId =',
        recurringGroupId,
      )
      const calendarRef = collection(
        db,
        'teachers',
        currentUser.uid,
        'calendar',
      )
      const q = query(
        calendarRef,
        where('recurringGroupId', '==', recurringGroupId),
      )
      const snapshot = await getDocs(q)
      console.log(
        '[RECURRING DELETE] fetchCount: total in series =',
        snapshot.size,
      )
      if (scope === 'all') {
        return snapshot.size
      }
      // 'future' — filter client-side: startAt >= selected lesson's startAt
      const selectedStartMs = new Date(lesson.startAt).getTime()
      console.log(
        '[RECURRING DELETE] fetchCount: selectedStartMs =',
        selectedStartMs,
        'from',
        lesson.startAt,
      )
      const futureCount = snapshot.docs.filter((docSnap) => {
        const data = docSnap.data()
        const docStartAt = data.startAt?.toDate
          ? data.startAt.toDate()
          : new Date(data.startAt)
        return docStartAt.getTime() >= selectedStartMs
      }).length
      console.log(
        '[RECURRING DELETE] fetchCount: future lessons (>= selected) =',
        futureCount,
      )
      return futureCount
    } catch (error: any) {
      console.error(
        '[RECURRING DELETE] fetchCount ERROR:',
        error?.message || error,
        error,
      )
      return 1
    }
  }
  const handleRecurringDelete = async () => {
    if (!lessonToDelete || !currentUser?.uid) return
    setIsRecurringDeleting(true)
    try {
      const recurringGroupId = (lessonToDelete as any).recurringGroupId
      console.log('[RECURRING DELETE] handleRecurringDelete called:', {
        scope: recurringDeleteScope,
        recurringGroupId,
        lessonId: lessonToDelete.id,
        startAt: lessonToDelete.startAt,
        startAtType: typeof lessonToDelete.startAt,
        uid: currentUser.uid,
      })
      if (recurringDeleteScope === 'this' || !recurringGroupId) {
        // Single lesson delete
        console.log(
          '[RECURRING DELETE] Single delete path — deleting lesson:',
          lessonToDelete.id,
        )
        await deleteLesson(lessonToDelete.id)
        toast.success('Lesson deleted')
      } else {
        // Batch delete for 'future' or 'all'
        console.log(
          '[RECURRING DELETE] Step 1: Building query for recurringGroupId:',
          recurringGroupId,
        )
        const calendarRef = collection(
          db,
          'teachers',
          currentUser.uid,
          'calendar',
        )
        const q = query(
          calendarRef,
          where('recurringGroupId', '==', recurringGroupId),
        )
        console.log('[RECURRING DELETE] Step 2: Executing getDocs query...')
        let snapshot
        try {
          snapshot = await getDocs(q)
          console.log(
            '[RECURRING DELETE] Step 2 ✅ Query returned:',
            snapshot.size,
            'docs',
          )
        } catch (queryError: any) {
          console.error(
            '[RECURRING DELETE] Step 2 ❌ getDocs FAILED:',
            queryError?.message || queryError,
            queryError,
          )
          throw new Error(
            `Firestore query failed: ${queryError?.message || 'Unknown query error'}`,
          )
        }
        // Determine which docs to delete
        console.log(
          '[RECURRING DELETE] Step 3: Filtering docs for scope:',
          recurringDeleteScope,
        )
        let docsToDelete = snapshot.docs
        if (recurringDeleteScope === 'future') {
          // Client-side filter: only delete lessons with startAt >= selected lesson
          const selectedStartMs = new Date(lessonToDelete.startAt).getTime()
          console.log(
            '[RECURRING DELETE] Step 3a: selectedStartMs =',
            selectedStartMs,
            'from startAt =',
            lessonToDelete.startAt,
          )
          docsToDelete = snapshot.docs.filter((docSnap) => {
            const data = docSnap.data()
            const docStartAt = data.startAt?.toDate
              ? data.startAt.toDate()
              : new Date(data.startAt)
            const docMs = docStartAt.getTime()
            console.log(
              '[RECURRING DELETE] Step 3b: doc',
              docSnap.id,
              'startAt =',
              docStartAt.toISOString(),
              'ms =',
              docMs,
              'include =',
              docMs >= selectedStartMs,
            )
            return docMs >= selectedStartMs
          })
          console.log(
            '[RECURRING DELETE] Step 3 ✅ Filtered to',
            docsToDelete.length,
            'future lessons. IDs:',
            docsToDelete.map((d) => d.id),
          )
        } else {
          console.log(
            '[RECURRING DELETE] Step 3 ✅ Deleting ALL in series:',
            docsToDelete.length,
            'IDs:',
            docsToDelete.map((d) => d.id),
          )
        }
        if (docsToDelete.length === 0) {
          console.warn(
            '[RECURRING DELETE] Step 4: No matching lessons found to delete',
          )
          toast.error('No matching lessons found')
        } else {
          // Batch delete (Firestore batch limit is 500, safe for recurring series)
          console.log(
            '[RECURRING DELETE] Step 4: Creating writeBatch for',
            docsToDelete.length,
            'docs',
          )
          try {
            const batch = writeBatch(db)
            docsToDelete.forEach((docSnap) => {
              console.log(
                '[RECURRING DELETE] Step 4a: Adding to batch — delete',
                docSnap.ref.path,
              )
              batch.delete(docSnap.ref)
            })
            console.log('[RECURRING DELETE] Step 4b: Committing batch...')
            await batch.commit()
            console.log(
              '[RECURRING DELETE] Step 4 ✅ Batch commit successful —',
              docsToDelete.length,
              'lessons deleted',
            )
            toast.success(
              `${docsToDelete.length} lesson${docsToDelete.length !== 1 ? 's' : ''} deleted`,
            )
          } catch (batchError: any) {
            console.error(
              '[RECURRING DELETE] Step 4 ❌ Batch delete FAILED:',
              batchError?.message || batchError,
              batchError,
            )
            throw new Error(
              `Batch delete failed: ${batchError?.message || 'Unknown batch error'}`,
            )
          }
        }
      }
      // Close modals and reset state
      setIsRecurringDeleteModalOpen(false)
      setLessonToDelete(null)
      setRecurringDeleteScope('this')
      setShowViewLessonModal(false)
      setViewingLesson(null)
    } catch (error: any) {
      console.error(
        '[RECURRING DELETE] ❌ FINAL ERROR:',
        error?.message || error,
        error,
      )
      console.error('[RECURRING DELETE] Error stack:', error?.stack)
      toast.error(
        `Failed to delete lessons: ${error?.message || 'Unknown error'}`,
      )
    } finally {
      setIsRecurringDeleting(false)
    }
  }
  // UPDATED: Render lesson popover without three-stripes icon
  const renderLessonPopover = () => {
    if (!popoverEvent) return null
    const bgColor =
      popoverEvent.color ||
      getInstrumentColor(popoverEvent.instrument || 'Other')
    const startLocal = toLocal(popoverEvent.startAt, DEFAULT_TIMEZONE)
    const endLocal = toLocal(popoverEvent.endAt, DEFAULT_TIMEZONE)
    const startTime = `${startLocal.getHours().toString().padStart(2, '0')}:${startLocal.getMinutes().toString().padStart(2, '0')}`
    const endTime = `${endLocal.getHours().toString().padStart(2, '0')}:${endLocal.getMinutes().toString().padStart(2, '0')}`
    const statusDisplay =
      popoverEvent.status?.charAt(0).toUpperCase() +
        popoverEvent.status?.slice(1).toLowerCase() || 'Confirmed'
    const getStatusTextColor = () => {
      switch (popoverEvent.status?.toUpperCase()) {
        case 'CONFIRMED':
          return 'text-[#10B981]'
        case 'PENDING':
          return 'text-[#EAB308]'
        case 'COMPLETED':
          return 'text-[#3B82F6]'
        case 'CANCELED':
          return 'text-[#EF4444]'
        default:
          return isDark ? 'text-gray-300' : 'text-gray-600'
      }
    }
    const notesPreview = popoverEvent.notes
      ? popoverEvent.notes.length > 80
        ? `${popoverEvent.notes.substring(0, 77)}...`
        : popoverEvent.notes
      : null
    const finalTop = popoverPosition.top + dragOffset.y
    const finalLeft = popoverPosition.left + dragOffset.x
    return (
      <div
        ref={popoverRef}
        className={`fixed z-50 w-80 rounded-xl shadow-2xl ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'} ${isDragging ? 'cursor-move' : ''}`}
        style={{
          top: `${finalTop}px`,
          left: `${finalLeft}px`,
          userSelect: isDragging ? 'none' : 'auto',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lesson-popover-title"
      >
        <div className="p-4">
          {/* Title - clean header without icon */}
          <div
            className="flex items-center mb-3 cursor-move"
            onMouseDown={handleDragStart}
            title="Drag to move"
          >
            <h3
              id="lesson-popover-title"
              className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}
            >
              {popoverEvent.title}
            </h3>
          </div>
          {/* Details */}
          <div
            className={`space-y-2 mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
          >
            <div className="flex items-center">
              <ClockIcon
                className={`h-4 w-4 mr-2 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <span className="text-sm">
                {formatTimeRange(startTime, endTime)}
              </span>
            </div>
            <div className="flex items-center">
              <UserIcon
                className={`h-4 w-4 mr-2 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <span className="text-sm">{popoverEvent.studentName}</span>
            </div>
            <div className="flex items-center">
              <MusicIcon
                className={`h-4 w-4 mr-2 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <span className="text-sm">{popoverEvent.instrument}</span>
            </div>
            {/* Notes row */}
            <div className="flex items-start pt-1">
              <BookIcon
                className={`h-4 w-4 mr-2 flex-shrink-0 mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <div className="flex-1">
                <span
                  className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Notes:
                </span>
                {notesPreview ? (
                  <p className="text-sm mt-0.5 line-clamp-2">{notesPreview}</p>
                ) : (
                  <p
                    className={`text-sm mt-0.5 italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                  >
                    No notes
                  </p>
                )}
              </div>
            </div>
            {/* Status row */}
            <div className="flex items-start pt-1">
              <InfoIcon
                className={`h-4 w-4 mr-2 flex-shrink-0 mt-0.5 ${getStatusTextColor().replace('text-', 'text-')}`}
              />
              <div className="flex-1">
                <span
                  className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                >
                  Status:
                </span>
                <p
                  className={`text-sm mt-0.5 font-medium ${getStatusTextColor()}`}
                >
                  {statusDisplay}
                </p>
              </div>
            </div>
          </div>
          {/* Actions - Duplicate and View Details buttons */}
          <div className="flex justify-end space-x-2">
            <button
              className={`px-4 py-2 rounded-md text-sm border-2 font-medium transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              onClick={(e) => {
                e.stopPropagation()
                setLessonToDuplicate(popoverEvent)
                // Pre-fill date and time
                const startLocal = toLocal(
                  popoverEvent.startAt,
                  DEFAULT_TIMEZONE,
                )
                setDuplicateDate(formatLocalDate(startLocal))
                setDuplicateTime(
                  `${String(startLocal.getHours()).padStart(2, '0')}:${String(startLocal.getMinutes()).padStart(2, '0')}`,
                )
                setIsDuplicateModalOpen(true)
                setTimeout(() => closePopover(), 50)
              }}
            >
              <div className="flex items-center">
                <CopyIcon className="h-4 w-4 mr-1.5" />
                Duplicate
              </div>
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm border-2 font-medium transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`}
              onClick={(e) => {
                e.stopPropagation()
                setViewingLesson(popoverEvent)
                setShowViewLessonModal(true)
                setTimeout(() => closePopover(), 50)
              }}
            >
              View Details
            </button>
          </div>
        </div>
      </div>
    )
  }
  // Add view state log before render
  console.log('[TC] view =', view)
  return (
    <CalendarErrorBoundary>
      <section
        className={`${isDark ? 'bg-navy-900/90 shadow-lg' : 'bg-white shadow-md'} rounded-xl p-6 ${isDark ? 'border border-white/10' : 'border border-gray-200'} relative`}
      >
        {/* Add custom styles for calendar cells */}
        <style jsx>{`
          .calendar-cell {
            transition: all 0.15s ease-out;
            position: relative;
          }
          .calendar-cell:hover::after {
            content: '';
            position: absolute;
            inset: 0;
            border-radius: 2px;
            box-shadow: inset 0 0 0 1px rgba(230, 57, 70, 0.3);
            pointer-events: none;
            z-index: 10;
          }
          @media (min-width: 768px) {
            .calendar-cell {
              border-radius: 8px;
            }
            .calendar-cell:hover::after {
              border-radius: 8px;
            }
          }
        `}</style>
        {/* Network offline warning */}
        {isOffline && (
          <div className="mb-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 flex items-start">
            <WifiOffIcon className="h-5 w-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4
                className={`${isDark ? 'text-white' : 'text-gray-800'} font-medium mb-1`}
              >
                You're offline
              </h4>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Your calendar can't be updated while offline. Please check your
                internet connection.
              </p>
            </div>
          </div>
        )}
        {/* Validation errors */}
        {renderValidationErrors()}
        {/* Loading state */}
        {(loading || authLoading) && renderLoadingState()}
        {/* Error state */}
        {!loading && !authLoading && error && renderAuthError()}
        {/* Calendar view */}
        {!loading && !authLoading && !error && (
          <>
            {view === 'month'
              ? renderMonthView()
              : view === 'week'
                ? renderWeekView()
                : renderDayView()}
          </>
        )}
        {/* Render the lesson popover */}
        {renderLessonPopover()}
        {/* Create/Reschedule Lesson Panel */}
        {showCreatePanel && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div
              ref={createPanelRef}
              className={`${isDark ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto`}
            >
              <div className="flex justify-between items-center mb-6">
                <h3
                  className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}
                >
                  Create New Lesson
                </h3>
                <button
                  onClick={() => {
                    setShowCreatePanel(false)
                    setNewLessonData({
                      title: 'Piano Lesson',
                      date: '',
                      startTime: '',
                      endTime: '',
                      studentName: '',
                      instrument: 'Piano',
                      notes: '',
                    })
                  }}
                  className={`${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  try {
                    await handleCreateLesson(newLessonData)
                    setShowCreatePanel(false)
                    setNewLessonData({
                      title: 'Piano Lesson',
                      date: '',
                      startTime: '',
                      endTime: '',
                      studentName: '',
                      instrument: 'Piano',
                      notes: '',
                    })
                  } catch (error) {
                    console.error('Error saving lesson:', error)
                    alert('Failed to save lesson. Please try again.')
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  >
                    Lesson Title
                  </label>
                  <input
                    type="text"
                    value={newLessonData.title}
                    onChange={(e) =>
                      setNewLessonData({
                        ...newLessonData,
                        title: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 rounded-md border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400 focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300'}`}
                    required
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  >
                    Student Name
                  </label>
                  <input
                    type="text"
                    value={newLessonData.studentName}
                    onChange={(e) =>
                      setNewLessonData({
                        ...newLessonData,
                        studentName: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 rounded-md border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400 focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300'}`}
                    required
                  />
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  >
                    Instrument
                  </label>
                  <select
                    value={newLessonData.instrument}
                    onChange={(e) =>
                      setNewLessonData({
                        ...newLessonData,
                        instrument: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 rounded-md border ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300'}`}
                  >
                    <option value="Piano">Piano</option>
                    <option value="Guitar">Guitar</option>
                    <option value="Violin">Violin</option>
                    <option value="Voice">Voice</option>
                    <option value="Drums">Drums</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  >
                    Date
                  </label>
                  <input
                    type="date"
                    value={newLessonData.date}
                    onChange={(e) =>
                      setNewLessonData({
                        ...newLessonData,
                        date: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 rounded-md ${isDark ? 'bg-navy-700 text-white border-navy-600' : 'bg-white text-gray-800 border-gray-300'} border`}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                    >
                      Start Time
                    </label>
                    <input
                      type="time"
                      value={newLessonData.startTime}
                      onChange={(e) =>
                        setNewLessonData({
                          ...newLessonData,
                          startTime: e.target.value,
                        })
                      }
                      className={`w-full px-3 py-2 rounded-md ${isDark ? 'bg-navy-700 text-white border-navy-600' : 'bg-white text-gray-800 border-gray-300'} border`}
                      required
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                    >
                      End Time
                    </label>
                    <input
                      type="time"
                      value={newLessonData.endTime}
                      onChange={(e) =>
                        setNewLessonData({
                          ...newLessonData,
                          endTime: e.target.value,
                        })
                      }
                      className={`w-full px-3 py-2 rounded-md ${isDark ? 'bg-navy-700 text-white border-navy-600' : 'bg-white text-gray-800 border-gray-300'} border`}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}
                  >
                    Notes (Optional)
                  </label>
                  <textarea
                    value={newLessonData.notes}
                    onChange={(e) =>
                      setNewLessonData({
                        ...newLessonData,
                        notes: e.target.value,
                      })
                    }
                    className={`w-full px-3 py-2 rounded-md border min-h-[80px] ${isDark ? 'bg-navy-900/50 border-navy-700 text-white placeholder-gray-400 focus:ring-deep-pink focus:border-deep-pink' : 'bg-white text-gray-800 border-gray-300'}`}
                    placeholder="Add any notes about this lesson..."
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreatePanel(false)
                      setNewLessonData({
                        title: 'Piano Lesson',
                        date: '',
                        startTime: '',
                        endTime: '',
                        studentName: '',
                        instrument: 'Piano',
                        notes: '',
                      })
                    }}
                    className={`px-4 py-2 rounded-lg ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 rounded-lg bg-[#10B981] hover:bg-[#059669] text-white flex items-center"
                  >
                    <SaveIcon className="h-4 w-4 mr-2" />
                    Create Lesson
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {/* Add the full lesson modal - now uses LessonDetailsModal instead of ViewLessonModal */}
        {showViewLessonModal && (
          <LessonDetailsModal
            isOpen={showViewLessonModal}
            lesson={convertLessonToDetails(viewingLesson)}
            onClose={() => {
              setShowViewLessonModal(false)
              setViewingLesson(null)
            }}
            darkMode={isDark}
            onUpdateLesson={async (lessonId, updates) => {
              try {
                console.log('[CALENDAR] 🔵 onUpdateLesson called with:', {
                  lessonId,
                  updates,
                  currentUser: currentUser?.uid,
                })
                // Ensure we have a valid teacher ID
                if (!currentUser?.uid) {
                  throw new Error('No teacher ID available')
                }
                console.log('[CALENDAR] 🔵 Calling updateLesson hook with:', {
                  lessonId,
                  teacherId: currentUser.uid,
                  updates,
                })
                // Call the hook's updateLesson function with teacherId
                await updateLesson(lessonId, updates)
                console.log(
                  '[CALENDAR] ✅ updateLesson hook completed successfully',
                )
                toast.success('Lesson updated successfully')
                // Close the modal
                setShowViewLessonModal(false)
                setViewingLesson(null)
                // The calendar will auto-refresh via onSnapshot in the hook
                console.log(
                  '[CALENDAR] ✅ Calendar will auto-refresh via listener',
                )
              } catch (error) {
                console.error('[CALENDAR] ❌ Error in onUpdateLesson:', error)
                const errorMessage =
                  error instanceof Error
                    ? error.message
                    : 'Failed to update lesson'
                toast.error(errorMessage)
                throw error
              }
            }}
            onDeleteLesson={async (lesson) => {
              // Handle lesson deletion
              // Check if this is a recurring lesson (viewingLesson has raw Firestore data)
              const rawLesson = viewingLesson
              const recurringGroupId = rawLesson
                ? (rawLesson as any).recurringGroupId
                : null
              if (recurringGroupId && rawLesson) {
                // Recurring lesson — show the recurring delete modal
                setLessonToDelete(rawLesson)
                setRecurringDeleteScope('this')
                setRecurringDeleteCount(1)
                setIsRecurringDeleteModalOpen(true)
                // Pre-fetch count for 'this' option
                fetchRecurringDeleteCount(rawLesson, 'this').then(
                  setRecurringDeleteCount,
                )
              } else {
                // Non-recurring — delete immediately
                console.log('Deleting lesson:', lesson)
                await deleteLesson(lesson.id)
                setShowViewLessonModal(false)
                setViewingLesson(null)
              }
            }}
          />
        )}

        {/* Recurring Delete Modal */}
        {isRecurringDeleteModalOpen && lessonToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-hidden">
            <div
              className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`}
            >
              {/* Header */}
              <div
                className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <AlertTriangleIcon className="h-5 w-5 text-red-500" />
                  </div>
                  <h3
                    className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}
                  >
                    Delete Recurring Lesson
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsRecurringDeleteModalOpen(false)
                    setLessonToDelete(null)
                    setRecurringDeleteScope('this')
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                <p
                  className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  This lesson is part of a recurring series. What would you like
                  to delete?
                </p>

                {/* Lesson info */}
                <div
                  className={`p-3 rounded-lg ${isDark ? 'bg-navy-900/50' : 'bg-gray-50'}`}
                >
                  <p
                    className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                  >
                    {lessonToDelete.title || 'Lesson'}
                  </p>
                  <p
                    className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {lessonToDelete.studentName}
                  </p>
                </div>

                {/* Radio options */}
                <div className="space-y-3">
                  {/* Option 1: This lesson only */}
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      recurringDeleteScope === 'this'
                        ? isDark
                          ? 'border-red-500/50 bg-red-500/5'
                          : 'border-red-400 bg-red-50'
                        : isDark
                          ? 'border-navy-600 hover:border-navy-500'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="recurringDeleteScope"
                      value="this"
                      checked={recurringDeleteScope === 'this'}
                      onChange={async () => {
                        setRecurringDeleteScope('this')
                        setRecurringDeleteCount(1)
                      }}
                      className="mt-0.5 accent-red-500"
                    />
                    <div>
                      <p
                        className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}
                      >
                        This lesson only
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        Only this single occurrence will be deleted
                      </p>
                    </div>
                  </label>

                  {/* Option 2: This and all future */}
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      recurringDeleteScope === 'future'
                        ? isDark
                          ? 'border-red-500/50 bg-red-500/5'
                          : 'border-red-400 bg-red-50'
                        : isDark
                          ? 'border-navy-600 hover:border-navy-500'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="recurringDeleteScope"
                      value="future"
                      checked={recurringDeleteScope === 'future'}
                      onChange={async () => {
                        setRecurringDeleteScope('future')
                        const count = await fetchRecurringDeleteCount(
                          lessonToDelete,
                          'future',
                        )
                        setRecurringDeleteCount(count)
                      }}
                      className="mt-0.5 accent-red-500"
                    />
                    <div>
                      <p
                        className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}
                      >
                        This and all future lessons
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        This lesson and all upcoming occurrences in the series
                      </p>
                    </div>
                  </label>

                  {/* Option 3: All lessons in series */}
                  <label
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                      recurringDeleteScope === 'all'
                        ? isDark
                          ? 'border-red-500/50 bg-red-500/5'
                          : 'border-red-400 bg-red-50'
                        : isDark
                          ? 'border-navy-600 hover:border-navy-500'
                          : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="recurringDeleteScope"
                      value="all"
                      checked={recurringDeleteScope === 'all'}
                      onChange={async () => {
                        setRecurringDeleteScope('all')
                        const count = await fetchRecurringDeleteCount(
                          lessonToDelete,
                          'all',
                        )
                        setRecurringDeleteCount(count)
                      }}
                      className="mt-0.5 accent-red-500"
                    />
                    <div>
                      <p
                        className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}
                      >
                        All lessons in the series
                      </p>
                      <p
                        className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        Every occurrence in this recurring series will be
                        deleted
                      </p>
                    </div>
                  </label>
                </div>

                <p
                  className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                >
                  This action cannot be undone.
                </p>
              </div>

              {/* Footer */}
              <div
                className={`flex justify-end gap-3 p-4 border-t ${isDark ? 'border-navy-700' : 'border-gray-200'}`}
              >
                <button
                  onClick={() => {
                    setIsRecurringDeleteModalOpen(false)
                    setLessonToDelete(null)
                    setRecurringDeleteScope('this')
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecurringDelete}
                  disabled={isRecurringDeleting}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {isRecurringDeleting ? (
                    <>
                      <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    `Delete ${recurringDeleteCount} lesson${recurringDeleteCount !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Duplicate Lesson Modal */}
        {isDuplicateModalOpen && lessonToDuplicate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm overflow-hidden">
            <div
              className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${isDark ? 'bg-navy-800 border border-navy-600' : 'bg-white border border-gray-200'}`}
            >
              <div
                className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-navy-700' : 'border-gray-200'}`}
              >
                <h3
                  className={`text-lg font-bold flex items-center ${isDark ? 'text-white' : 'text-gray-800'}`}
                >
                  <CopyIcon className="h-5 w-5 mr-2 text-[#10B981]" />
                  Duplicate Lesson
                </h3>
                <button
                  onClick={() => {
                    setIsDuplicateModalOpen(false)
                    setLessonToDuplicate(null)
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-navy-700' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'}`}
                >
                  <XIcon className="h-5 w-5" />
                </button>
              </div>

              <form
                onSubmit={handleDuplicateLesson}
                className="p-4 space-y-4 min-w-0 overflow-hidden"
              >
                {/* Pre-filled Info */}
                <div
                  className={`p-3 rounded-lg ${isDark ? 'bg-navy-900/50' : 'bg-gray-50'} space-y-2 text-sm`}
                >
                  <div className="flex justify-between">
                    <span
                      className={isDark ? 'text-gray-400' : 'text-gray-500'}
                    >
                      Student:
                    </span>
                    <span
                      className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {lessonToDuplicate.studentName}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDark ? 'text-gray-400' : 'text-gray-500'}
                    >
                      Instrument:
                    </span>
                    <span
                      className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {lessonToDuplicate.instrument}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDark ? 'text-gray-400' : 'text-gray-500'}
                    >
                      Duration:
                    </span>
                    <span
                      className={`font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}
                    >
                      {lessonToDuplicate.durationMin ||
                        lessonToDuplicate.durationMinutes ||
                        60}{' '}
                      mins
                    </span>
                  </div>
                </div>

                {/* Editable Fields */}
                <div className="space-y-4 min-w-0">
                  <div className="min-w-0 overflow-hidden">
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      New Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={duplicateDate}
                      onChange={(e) => setDuplicateDate(e.target.value)}
                      className={`w-full min-w-0 box-border px-3 py-2 rounded-md border appearance-none ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-[#10B981] focus:border-[#10B981]' : 'bg-white text-gray-800 border-gray-300 focus:ring-[#10B981] focus:border-[#10B981]'}`}
                      style={{ maxWidth: 'calc(100%)' }}
                    />
                  </div>

                  <div className="min-w-0 overflow-hidden">
                    <label
                      className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      required
                      value={duplicateTime}
                      onChange={(e) => setDuplicateTime(e.target.value)}
                      className={`w-full min-w-0 box-border px-3 py-2 rounded-md border appearance-none ${isDark ? 'bg-navy-900/50 border-navy-700 text-white focus:ring-[#10B981] focus:border-[#10B981]' : 'bg-white text-gray-800 border-gray-300 focus:ring-[#10B981] focus:border-[#10B981]'}`}
                      style={{ maxWidth: 'calc(100%)' }}
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsDuplicateModalOpen(false)
                      setLessonToDuplicate(null)
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-navy-700 hover:bg-navy-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isDuplicating || !duplicateDate}
                    className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center border-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`}
                  >
                    {isDuplicating ? (
                      <>
                        <LoaderIcon className="h-4 w-4 mr-2 animate-spin" />
                        Duplicating...
                      </>
                    ) : (
                      'Duplicate Lesson'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </section>
    </CalendarErrorBoundary>
  )
}
